import { useEffect, useState, useCallback } from "react";
import { Plus, Edit2, Trash2 } from "lucide-react";
import { toast } from "sonner";
import AdminLayout from "../components/AdminLayout";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Switch } from "../components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "../components/ui/select";
import { api, formatApiErrorDetail } from "../lib/api";

const EMPTY = { title: "", image_url: "", link: "", position: "home_top", active: true, sort_order: 100 };

export default function AdminBanners() {
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [openForm, setOpenForm] = useState(false);

  const load = useCallback(async () => {
    const { data } = await api.get("/admin/banners");
    setItems(data.items);
  }, []);
  useEffect(() => { load(); }, [load]);

  const openCreate = () => { setEditing(null); setForm({ ...EMPTY }); setOpenForm(true); };
  const openEdit = (b) => { setEditing(b.id); setForm({ title: b.title, image_url: b.image_url, link: b.link || "", position: b.position, active: !!b.active, sort_order: b.sort_order ?? 100 }); setOpenForm(true); };

  const save = async () => {
    try {
      if (editing) await api.patch(`/admin/banners/${editing}`, form);
      else await api.post("/admin/banners", form);
      toast.success(editing ? "Banner updated" : "Banner added");
      setOpenForm(false); load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
  };
  const toggleActive = async (b) => {
    try { await api.patch(`/admin/banners/${b.id}`, { ...b, active: !b.active }); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
  };
  const del = async (id) => {
    if (!window.confirm("Delete this banner?")) return;
    try { await api.delete(`/admin/banners/${id}`); toast.success("Deleted"); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
  };

  const upd = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  return (
    <AdminLayout title="Banners" subtitle={`${items.length} banner${items.length === 1 ? "" : "s"}`} action={
      <Button data-testid="admin-add-banner-btn" onClick={openCreate} className="rounded-full"><Plus className="h-4 w-4 mr-1" />Add Banner</Button>
    }>
      <div data-testid="admin-banners-list" className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {items.map((b) => (
          <div key={b.id} data-testid={`banner-card-${b.id}`} className="bg-white border border-border rounded-2xl overflow-hidden">
            <img src={b.image_url} alt={b.title} className="w-full h-32 object-cover" />
            <div className="p-3 space-y-2">
              <div className="flex items-start justify-between gap-2">
                <div className="font-medium">{b.title}</div>
                <span className="text-[10px] uppercase tracking-wide font-semibold px-2 py-0.5 rounded-full bg-secondary">{b.position}</span>
              </div>
              <div className="text-xs text-muted-foreground">Order: {b.sort_order} · {b.link || "no link"}</div>
              <div className="flex items-center justify-between">
                <label className="flex items-center gap-2 text-xs">
                  <Switch data-testid={`banner-active-${b.id}`} checked={b.active} onCheckedChange={() => toggleActive(b)} />Active
                </label>
                <div>
                  <Button size="sm" variant="ghost" data-testid={`banner-edit-${b.id}`} onClick={() => openEdit(b)}><Edit2 className="h-4 w-4" /></Button>
                  <Button size="sm" variant="ghost" data-testid={`banner-del-${b.id}`} onClick={() => del(b.id)}><Trash2 className="h-4 w-4 text-rose-600" /></Button>
                </div>
              </div>
            </div>
          </div>
        ))}
        {items.length === 0 && <div className="text-sm text-muted-foreground col-span-full">No banners yet.</div>}
      </div>

      <Dialog open={openForm} onOpenChange={setOpenForm}>
        <DialogContent data-testid="banner-form-modal" className="sm:max-w-xl rounded-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader><DialogTitle>{editing ? "Edit Banner" : "Add Banner"}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div className="space-y-1.5"><label className="text-xs font-medium">Title</label>
              <Input data-testid="banner-form-title" value={form.title} onChange={upd("title")} /></div>
            <div className="space-y-1.5"><label className="text-xs font-medium">Image URL</label>
              <Input data-testid="banner-form-image" value={form.image_url} onChange={upd("image_url")} placeholder="https://..." />
              {form.image_url && <img src={form.image_url} alt="" className="mt-1 w-full h-32 rounded-xl object-cover" />}
            </div>
            <div className="space-y-1.5"><label className="text-xs font-medium">Link (optional)</label>
              <Input data-testid="banner-form-link" value={form.link} onChange={upd("link")} placeholder="/patient/doctors" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5"><label className="text-xs font-medium">Position</label>
                <Select value={form.position} onValueChange={(v) => setForm((f) => ({ ...f, position: v }))}>
                  <SelectTrigger data-testid="banner-form-position"><SelectValue /></SelectTrigger>
                  <SelectContent><SelectItem value="home_top">home_top</SelectItem><SelectItem value="home_mid">home_mid</SelectItem></SelectContent>
                </Select></div>
              <div className="space-y-1.5"><label className="text-xs font-medium">Sort order</label>
                <Input type="number" data-testid="banner-form-sort" value={form.sort_order} onChange={(e) => setForm((f) => ({ ...f, sort_order: Number(e.target.value) || 0 }))} /></div>
            </div>
            <label className="flex items-center gap-2 text-sm">
              <Switch data-testid="banner-form-active" checked={form.active} onCheckedChange={(v) => setForm((f) => ({ ...f, active: v }))} />Active
            </label>
          </div>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setOpenForm(false)} className="rounded-full">Cancel</Button>
            <Button data-testid="banner-save-btn" onClick={save} disabled={!form.title || !form.image_url} className="rounded-full bg-primary hover:bg-primary/90 text-primary-foreground hover:text-primary-foreground">{editing ? "Save" : "Add"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </AdminLayout>
  );
}
